import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";

import {
    Tab,
    TabList,
    Button,
    Menu,
    MenuItem,
    Overflow,
    OverflowItem,
    useOverflowMenu,
    useIsOverflowItemVisible,
    MenuTrigger,
    MenuList,
    MenuPopover,
    MessageBar,
    MessageBarBody,
    MessageBarActions,
} from "@fluentui/react-components";
import { MoreHorizontalRegular, DismissRegular } from "@fluentui/react-icons";
import { useMsal } from '@azure/msal-react';

import { appTheme } from './Helpers/customRevealTheme';
import { darkPowerONTheme, brandPowerON } from './Helpers/fluentPowerONTheme';

import { useSignInState } from './Components/signInState';
import LoadingPage from './Components/LoadingPage';
import DashboardLoader from './Components/DashboardLoader';
import useFetch from './Hooks/useFetchAPI';
import useRevealHeaders from './Hooks/useRevealHeaders';
import { addAlertMenuItemToVisualization } from './Helpers/utils';
import useAlerts from './Hooks/useAlerts';
import AlertModal from './Components/AlertModal';
import { Alert } from './Types/alert';

// import './IntuneDashboards.css'

declare let $: any;

const ViewDashboard = (props: any) => {
    const dashboardURL = `${process.env.REACT_APP_REVEAL_DATA_URL}dashboards`;
    $.ig.RevealSdkSettings.setBaseUrl(process.env.REACT_APP_REVEAL_BASE_URL);
    $.ig.RevealSdkSettings.enableActionsOnHoverTooltip = true;

    const { inProgress } = useMsal();
    const { signInState } = useSignInState();
    const { tab } = useParams();
    const [dashboardId, setDashboardId] = useState(tab || "Home"); // Default to "Home"
    const [dashboards, setDashboards] = useState<DashboardDataResult[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDashboardLoading, setIsDashboardLoading] = useState(false);
    const { headers, getRevealHeaders } = useRevealHeaders();
    const { currentAlert, setCurrentAlert, createAlert, createAlertData } = useAlerts();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    const { fetchAPI: fetchDashboards, apiData: dashboardData, isLoading: isLoadingDashboards, serverError: serverErrorDashboards } = useFetch({ scope: 'AppUser' })

    useEffect(() => {
        $.ig.RevealSdkSettings.theme = appTheme(signInState.userInfo[0]?.ThemePreference);
        darkPowerONTheme.colorBrandForeground1 = brandPowerON[110]; // use brand[110] instead of brand[100]
        darkPowerONTheme.colorBrandForeground2 = brandPowerON[120]; // use brand[120] instead of brand[110]
    }, [signInState.userInfo[0]?.ThemePreference]);

    const navigate = useNavigate();

    const changeDashboard = (dashboardName: string) => (e: any) => {
        setDashboardId(dashboardName || 'Home');
        navigate(`/intunedashboards/${encodeURIComponent(dashboardName)}`);
    };

    useEffect(() => {
        $.ig.RevealSdkSettings.setAdditionalHeadersProvider(() => headers);
    }, [headers]);

    useEffect(() => {
        const revealHeaders = async () => {
            await getRevealHeaders();
        };
        if (inProgress === 'none') {
            setIsLoading(true);
            revealHeaders()
                .then(() => {
                    setIsLoading(false);
                });
        }
    }, [inProgress, props.currentCustomerID]);

    useEffect(() => {
        setDashboards([])
        const fetchDashList = async () => {
            await fetchDashboards({ url: dashboardURL, method: 'GET' });
        };
        fetchDashList()
            .catch((error) => {
                console.error('Error fetching dashboards', error);
                setDashboards([]);
            });
    }, [props.currentCustomerID]);

    const updateRevealView = (revealView: any) => {
        revealView.canEdit = false;
        revealView.canSaveAs = false;

        revealView.onLinkedDashboardProviderAsync = (dashboardId: string) => {
            return $.ig.RVDashboard.loadDashboard(dashboardId);
        };
        revealView.onUrlLinkRequested = function (args: any) {
            if (args?.url?.includes('DashboardLink')) {
                const hasValue = args.row.find((row: any) => {
                    return row.__columnName === 'DashboardLink' && row.__value !== null;
                });

                if (!hasValue) {
                    console.warn('No DashboardLink value was found in row:', args.row);
                    return '';
                }
            }
            return args.url;
        };
        addAlertMenuItemToVisualization(revealView, signInState, setCurrentAlert);
    }

    useEffect(() => {
        if (currentAlert) {
            setIsModalOpen(true);
        }
    }, [currentAlert]);

    useEffect(() => {
        if (!isLoading) {
            // Set loading state when starting to load dashboard
            setIsDashboardLoading(true);

            $.ig.RVDashboard.loadDashboard(dashboardId).then((dashboard: any) => {
                const revealView = new $.ig.RevealView('#revealView');
                revealView.dashboard = dashboard;
                updateRevealView(revealView);

                // Clear loading state
                setIsDashboardLoading(false);
            })
                .catch((error: any) => {
                    console.error('Error loading dashboard', error);

                    // Clear loading state
                    setIsDashboardLoading(false);
                });
        }
    }, [dashboardId, isLoading, props.currentCustomerID]);

    useEffect(() => {
        console.log('[IntuneDashboards] dashboards:', dashboardData);
        setDashboards(dashboardData as unknown as DashboardDataResult[]);
    }, [dashboardData]);

    // Define tab items as an array for easier mapping.
    const allowedDashboards = [
        'Home', 'Windows Update Insights', 'Feature Update Insights', 'Environment Compliance', 'NIST Application Vulnerabilities',
        'Cyber Essentials', 'Application Management', 'Asset Insights', 'Autopilot Insights', 'Mobile Device Insights', 'Audit Insights',
        'Windows 365 - Operational', 'Windows 365 - Optimization', 'Windows 365 - Planning',
    ];
    const tabItems = allowedDashboards.map((item, index) => ({
        key: item,
        label: item,
        tabIndex: index + 1
    }));
    // Helper component: Renders a MenuItem for a given tab if it isn’t visible.
    const OverflowMenuItem = (props: { tab: typeof tabItems[0]; onClick: () => void }) => {
        const { tab, onClick } = props;
        const isVisible = useIsOverflowItemVisible(tab.key);
        if (isVisible) return null;
        return (
            <MenuItem key={tab.key} onClick={onClick}>
                {tab.label}
            </MenuItem>
        );
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentAlert(null);
    };

    useEffect(() => {
        if (createAlertData) {
            setNotificationMessage('Alert created successfully');
        }
    }, [createAlertData]);

    const handleSaveAlert = async (updatedAlert: any) => {
        try {
            await createAlert(updatedAlert);
            handleCloseModal();
        } catch (error) {
            console.error('Error saving alert:', error);
        }
    };

    // Overflow menu component that appears when not all tabs are visible.
    const OverflowMenu = (props: { onTabSelect: (tabname: string) => void }) => {
        const { onTabSelect } = props;
        const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();

        if (!isOverflowing) return null;

        return (
            <Menu hasIcons>
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        appearance="transparent"
                        ref={ref}
                        icon={<MoreHorizontalRegular />}
                        aria-label={`${overflowCount} more tabs`}
                        role="tab"
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {tabItems.map((tab) => (
                            <OverflowMenuItem key={tab.key} tab={tab} onClick={() => onTabSelect(tab.key)} />
                        ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        );
    };

    return (
        <div className='Dashboard'>
            {notificationMessage && (
                <MessageBar intent={'success'}>
                    <MessageBarBody>
                        {notificationMessage}
                    </MessageBarBody>
                    <MessageBarActions
                        containerAction={
                            <Button
                                onClick={() => setNotificationMessage('')}
                                aria-label="dismiss"
                                appearance="transparent"
                                icon={<DismissRegular />}
                            />
                        }
                    />
                </MessageBar>
            )}
            {isLoadingDashboards && <LoadingPage message="Loading Dashboards..." />}
            {!isLoadingDashboards && dashboards &&
                <>
                    <div style={{ overflowX: 'auto' }}>
                        <Overflow minimumVisible={2} padding={250}>
                            <TabList
                                selectedValue={dashboardId}
                                onTabSelect={(_, data) => changeDashboard(data.value as string)(null)}
                            >
                                {tabItems.map((item) => (
                                    <OverflowItem
                                        key={item.key}
                                        id={item.key}
                                        priority={item.key === dashboardId ? 2 : 1}
                                    >
                                        <Tab tabIndex={item.tabIndex} value={item.key}>
                                            {item.label}
                                        </Tab>
                                    </OverflowItem>
                                ))}
                                {/* Overflow menu for items that do not fit */}
                                <OverflowMenu onTabSelect={(tabName) => changeDashboard(tabName)(null)} />
                            </TabList>
                        </Overflow>
                    </div>

                    <div hidden={isDashboardLoading} id='revealView' style={{ height: 'calc(100vh - 130px)', width: '100%' }}></div>

                    <DashboardLoader isLoading={isDashboardLoading} loadingText="Loading dashboard data ..." />
                </>
            }
            {currentAlert && (
                <AlertModal
                    alert={currentAlert as Alert}
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSave={handleSaveAlert}
                    title={`Create Alert: ${currentAlert.AlertTitle}`}
                />
            )}
        </div>
    );
};

export default ViewDashboard;